import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {
    getCertificate,
    getProviderConnectionCapabilities,
    listCertificates,
    listProviderConnectionsForCertificates,
} from './queries';

import {
    deleteCertificate,
    requestCertificate,
} from './mutations';

import {
    Certificate,
    CertificateConnectionResult,
    DeleteCertificate,
    DeleteCertificateInput,
    DeleteCertificateResult,
    GetCertificate,
    ListCertificates,
    RequestCertificate,
    RequestCertificateInput,
} from '../types';

const client = generateClient();

// FNP-1193 — the certificates service.
//
// ⚠️ ERRORS ARE RE-THROWN, NEVER SWALLOWED. Catching and returning undefined
// makes a failed fetch indistinguishable from a tenant with no certificates:
// `isError` stays false and the table renders its empty state, so "the API is
// down" and "you have no certificates" look identical. Those are two of the
// three states this module most needs to tell apart.
//
// On the WRITE path the reason is sharper. A refused request carries the
// provider's own words — an AccessDenied naming `acm:RequestCertificate` above
// all — and that refusal is the most valuable thing the call produced. The
// mutation hook classifies it; stripping it here would leave nothing to
// classify and the tenant would be told "something went wrong" about a problem
// with a precise fix.

/**
 * What a connection declares about writing, as this module needs it.
 *
 * ⚠️ ONE FIELD, AND IT IS THE ONE THE BACKEND ACTUALLY GATES ON.
 * `flexi-domains-api/src/certificate/src/main.ts` builds its issuer with
 * `{canRequest: connection.writable === true}` and consults no other field, so
 * this mirrors the real gate rather than approximating it from the DNS
 * capability snapshot. See queries.ts for why the obvious alternative
 * (`canWriteRecords`) would have been a proxy for the wrong thing.
 */
export type CertificateWriteCapabilities = {
    // ⚠️ DECLARED BY THE TENANT, NOT PROBED. True means "the tenant said this
    // role can write", not "this role provably can". A request may still be
    // refused at ACM.
    writable: boolean,
}

/** A connection as the REQUEST FORM's picker needs it. */
export type CertificateConnection = {
    id: string,
    // What the tenant named it. The picker shows this, never the opaque id.
    displayName: string,
    // The catalog provider id, e.g. 'ROUTE53'.
    providerId: string,
    // ⚠️ Only 'ACTIVE' may be offered. Any other status is the provider saying
    // the credential does not work NOW, whatever was declared at connect time.
    status: string,
    // ⚠️ NON-NULL ON THE SCHEMA (`Boolean!`), defaulted false server-side —
    // "unknown" is deliberately not representable, because a client reading
    // unknown as "probably yes" is how a control appears in front of a user
    // that fails after they filled in a form.
    writable: boolean,
}

type GetProviderConnectionCapabilities = {
    getProviderConnection?: {
        id: string,
        status: string,
        writable?: boolean | null,
    } | null
}

type ListProviderConnections = {
    listProviderConnections?: {
        items?: {
            id: string,
            displayName: string,
            providerId: string,
            status: string,
            writable?: boolean | null,
        }[] | null,
        nextToken?: string | null,
    } | null
}

/**
 * One page of THIS TENANT'S certificates.
 *
 * ⚠️ NO TENANT ARGUMENT IS SENT. `listCertificates` takes only `limit` and
 * `nextToken`; the tenant is a key condition built server-side from the
 * caller's own claim. There is nothing to pass and nothing to get wrong.
 *
 * ⚠️ EVERY ITEM'S `status` WAS READ THROUGH TO THE PROVIDER to answer this
 * call. That is the accepted N+1 (bounded by certificates-per-tenant, a number
 * in the tens) and it must not be "optimized" into a cached projection —
 * doing so would make `status` a value that can disagree with the provider.
 */
export const fetchCertificates = async (
    limit: number,
    nextToken: string | null,
): Promise<CertificateConnectionResult> => {

    const response = await client.graphql<GraphQLQuery<ListCertificates>>({
        query: listCertificates,
        variables: {
            limit,
            nextToken,
        },
    });

    return {
        items: response.data?.listCertificates?.items ?? [],
        // ⚠️ `?? null` and NOT `|| null`: coercing an empty-string token to
        // null would end pagination early.
        nextToken: response.data?.listCertificates?.nextToken ?? null,
    };
};

/** One certificate, by Flexi's own id. */
export const fetchCertificate = async (
    id: string,
): Promise<Certificate | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetCertificate>>({
        query: getCertificate,
        variables: {id},
    });

    return response.data?.getCertificate ?? undefined;
};

/**
 * The connections a certificate may be requested through.
 *
 * Returns them ALL, unfiltered — the filtering rule lives in the hook next to
 * its own test, because "which connections may be offered" is a decision worth
 * testing directly rather than through a network mock.
 */
export const fetchConnections = async (): Promise<CertificateConnection[]> => {

    const response = await client.graphql<GraphQLQuery<ListProviderConnections>>({
        query: listProviderConnectionsForCertificates,
        variables: {limit: 100},
    });

    const items = response.data?.listProviderConnections?.items ?? [];

    return items.map(item => ({
        id: item.id,
        displayName: item.displayName,
        providerId: item.providerId,
        status: item.status,
        // Explicitly `=== true`: the field is non-null on the schema, but a
        // partial response must not read as permission.
        writable: item.writable === true,
    }));
};

/**
 * What this connection's credential can do, as this module needs it.
 *
 * Undefined means "could not be determined", and every caller treats that as
 * NOT permitted.
 *
 * ⚠️ A CONNECTION THAT IS NOT ACTIVE REPORTS NOTHING, whatever its stored
 * snapshot says. The snapshot records what the credential proved AT CONNECT
 * TIME; an INVALID or UNREACHABLE status is the provider saying that credential
 * does not work NOW.
 */
export const fetchConnectionCapabilities = async (
    connectionId: string,
): Promise<CertificateWriteCapabilities | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetProviderConnectionCapabilities>>({
        query: getProviderConnectionCapabilities,
        variables: {id: connectionId},
    });

    const connection = response.data?.getProviderConnection;

    if (!connection) {
        return undefined;
    }

    if (connection.status !== 'ACTIVE') {
        return undefined;
    }

    return {
        // Explicitly `=== true`: a null or absent value is not permission.
        writable: connection.writable === true,
    };
};

/**
 * Request a certificate, and write its validation record into the owning zone.
 *
 * ⚠️ THE RESULT IS NULLABLE ON THE SCHEMA and that is not an oversight: a
 * non-null field resolving null nulls its whole parent on this platform, so the
 * honest failure mode is an error entry plus null rather than a fabricated
 * certificate. `undefined` here means the request reported nothing, and the
 * caller must not invent a certificate for it.
 *
 * ⚠️ A RETURNED CERTIFICATE IS NOT AN ISSUED ONE. It comes back
 * PENDING_VALIDATION at best. Read `status` before telling anyone anything.
 */
export const requestNewCertificate = async (
    input: RequestCertificateInput,
): Promise<Certificate | undefined> => {

    const response = await client.graphql<GraphQLQuery<RequestCertificate>>({
        query: requestCertificate,
        variables: {input},
    });

    return response.data?.requestCertificate ?? undefined;
};

/**
 * Delete a certificate.
 *
 * ⚠️ THE PROVIDER DECIDES, AND A REFUSAL IS NOT AN ERROR HERE.
 *
 * An in-use refusal comes back as a RESOLVED result with `deleted: false` and
 * `inUse: true` — not as a thrown GraphQL error. So this resolving successfully
 * proves nothing about whether the certificate is gone, and every caller must
 * read `deleted` rather than treating a resolution as success.
 *
 * Flexi does NOT pre-check against local state before calling. Local state goes
 * stale, the check would need its own network call to be worth anything, and it
 * would make this code the authority on a rule the provider owns.
 */
export const removeCertificate = async (
    input: DeleteCertificateInput,
): Promise<DeleteCertificateResult | undefined> => {

    const response = await client.graphql<GraphQLQuery<DeleteCertificate>>({
        query: deleteCertificate,
        variables: {input},
    });

    return response.data?.deleteCertificate ?? undefined;
};
